import { useMemo, useState, useCallback } from "react";
import TaskService, { TaskFieldId } from "../../../services/TaskSerivce";
import { TranslationService } from "../../../services/TranslationService";
import { useEndpointData } from "../../../utils/TableUtils";
import { TaskListRequest } from "../../task/entities/TaskListRequest";
import CompanyService from "../../../services/CompanyService";
import AdvancedFilterService, {
  Filter,
  FilterLine
} from "../../../services/AdvancedFilterService";
import FilterService, {
  FilterEntity,
  FilterOption
} from "../../../services/FilterService";
import moment from "moment";
import {
  formatDateDigits,
  formatIntizaDate,
  formatTimeIfNonZero
} from "../../../utils/FormatUtils";
import { DateService } from "../../../services/DateService";
import { TaskEntities } from "../../task/entities/TaskListResponse";
import { isNullOrWhitespace, OptionalMap } from "../../../utils/Utils";
import { TaskStatus } from "../../task/entities/TaskStatus";
import Loading from "../../shared/components/Loading";
import TooltipComponent from "../../shared/TooltipComponent";
import notCheckedIcon from "../../../assets/img/not-checked-icon.svg";
import { Link } from "react-router-dom";

const CalendarTask = () => {
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(
    moment().startOf("day").toDate()
  );

  const [request, setRequest] = useState(getDefaultFilter);
  const [onlyMyTasks, setOnlyMyTasks] = useState(true);
  const endpoint = useCallback(() => TaskService.getList(request), [request]);
  const { response, loading } = useEndpointData(endpoint);

  const userId = CompanyService.getUserid();
  const monthIssuesToShow = useMemo(() => {
    return (
      response?.list.filter(
        (x) => !onlyMyTasks || x.ResponsibleUserID?.toString() === userId
      ) ?? []
    );
  }, [response?.list, onlyMyTasks, userId]);

  const tasksToShow = useMemo(() => {
    if (selectedDates.length === 0) {
      return monthIssuesToShow;
    }
    return monthIssuesToShow.filter((x) =>
      selectedDates.some((d) => moment(x.dueDate).isSame(d, "day"))
    );
  }, [monthIssuesToShow, selectedDates]);

  const toggleDate = (date: Date) => {
    if (!moment(date).isSame(selectedMonth, "month")) {
      changeMonth(date);
    }
    setSelectedDates((prev) => {
      const exists = prev.some((d) => moment(d).isSame(date, "day"));
      if (exists) {
        return prev.filter((d) => !moment(d).isSame(date, "day"));
      }
      return [...prev, date];
    });
  };

  const changeMonth = (date: Date) => {
    if (!moment(date).isSame(selectedMonth, "month")) {
      changeMonthTo(request, date);
      setRequest({ ...request });
      setSelectedMonth(date);
      setSelectedDates([]);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="row flex-nowrap">
          <div className="col-auto">
            <CalendarView
              selectedDates={selectedDates}
              selectedMonth={selectedMonth}
              onDateClick={toggleDate}
              onMonthChange={changeMonth}
              issues={monthIssuesToShow}
            />
          </div>
          <div className="col">
            <div className="p-0 h-100">
              <section className="h-100">
                {loading ? (
                  <div className="d-flex justify-content-center h-100">
                    <Loading />
                  </div>
                ) : (
                  <>
                    <ul className="nav nav-pills" role="tablist">
                      <li className="nav-item" role="presentation">
                        <button
                          className={
                            "nav-link " + (onlyMyTasks ? "active" : "")
                          }
                          type="button"
                          role="tab"
                          onClick={() => setOnlyMyTasks(true)}
                        >
                          <p>{TranslationService.translate.MyTasks}</p>
                          <span className="rounded"></span>
                        </button>
                      </li>
                      <li className="nav-item" role="presentation">
                        <button
                          className={
                            "nav-link " + (!onlyMyTasks ? "active" : "")
                          }
                          type="button"
                          role="tab"
                          onClick={() => setOnlyMyTasks(false)}
                        >
                          <p>{TranslationService.translate.AllTasks}</p>
                          <span className="rounded"></span>
                        </button>
                      </li>
                    </ul>
                    <div className="tab-content tasks" id="pills-tab-content">
                      <TaskList tasks={tasksToShow} />
                    </div>
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function getDefaultFilter() {
  const taskListRequest = new TaskListRequest(
    null,
    null,
    CompanyService.getAdditionalDefinitions()
  );
  taskListRequest.pagesize = 1000;
  changeMonthTo(taskListRequest, new Date());
  return taskListRequest;
}

function changeMonthTo(request: TaskListRequest, date: Date) {
  const filterOptions = FilterService.GetFiltersForEntity(FilterEntity.Task);
  const filters = AdvancedFilterService.parseFilters(request.filter, [
    filterOptions
  ]);
  const dateFilter: Filter =
    filters.find((x) => x.id === TaskFieldId.Date.toString()) ??
    getDefaultTaskDateFilter(filterOptions);
  const newFilters = filters.filter(
    (x) => x.id === TaskFieldId.Date.toString()
  ) as Filter[];
  newFilters.push(dateFilter);

  const startOfMonth = moment(date).startOf("month").toDate();
  const endOfMonth = moment(date).endOf("month").toDate();
  dateFilter.value =
    formatIntizaDate(startOfMonth) + "-" + formatIntizaDate(endOfMonth);
  request.filter = decodeURIComponent(
    AdvancedFilterService.filtersToRequestString(newFilters)
  );
}

const TaskList = ({ tasks }: { tasks: TaskEntities.ListItem[] }) => {
  const getName = (userId: number | string | undefined) => {
    return CompanyService.getUsers().find((x) => x.Id === userId?.toString())
      ?.Value;
  };
  return (
    <ul className="d-flex flex-column">
      {tasks.map((x) => (
        <li className="historyCard" key={x.TaskID}>
          <div className="row align">
            <div className="col-auto pe-0">
              <div className="icon pointer">
                {x.status === TaskStatus.Fulfilled ? (
                  <img
                    className="img-fluid"
                    src="/static/media/taskIcon.dfcfd10f.svg"
                    alt="icon comment"
                  />
                ) : (
                  <img
                    className="img-fluid"
                    src={notCheckedIcon}
                    alt="icon comment"
                  />
                )}
              </div>
            </div>
            <Link
              className="col d-flex align-items-center justify-content-between pointer text-reset text-decoration-none"
              to={`/${TranslationService.currentLanguage}/client/details?id=${x.PersonID}`}
              replace
            >
              <div className="d-flex align-items-center flex-shrink-0 w-100">
                <div className="type d-flex align-items-center flex-shrink-0 pb-1 nameCalendarItem">
                  {x.Person.Name}
                </div>
                {OptionalMap(getName(x.ResponsibleUserID), (name) => (
                  <span className="creator flex-shrink-0 ps-1">({name})</span>
                ))}
                :
                <TooltipComponent title={x.Description}>
                  <span className="truncate-lh-1 px-1">{x.Description}</span>
                </TooltipComponent>
                <div className="d-flex align-items-center flex-shrink-0 ms-auto gap-1">
                  <div className="ms-2 d-flex align-items-center gap-2">
                    {x.HasAttachments && (
                      <TooltipComponent
                        title={TranslationService.translate.Attachments}
                      >
                        <i className="fa-light fa-paperclip text-grey"></i>
                      </TooltipComponent>
                    )}
                    {!isNullOrWhitespace(x.Tags) && (
                      <TooltipComponent
                        title={TranslationService.translate.Tags}
                      >
                        <i
                          className="fa fa-tags activity-icon-color my-2"
                          onClick={() => {}}
                        />
                      </TooltipComponent>
                    )}
                  </div>
                  <div
                    className={
                      "dateSecondary" + (x.dueDays ? " text-danger" : "")
                    }
                  >
                    {OptionalMap(
                      new Date(x.dueDate),
                      (x) => formatDateDigits(x) + " " + formatTimeIfNonZero(x)
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </li>
      ))}
      {tasks.length === 0 && (
        <div
          className="h-50 d-flex align-items-center justify-content-center"
          style={{ minHeight: 200 }}
        >
          <p className="lead">
            {TranslationService.translate.NoTasksInThisPeriod}
          </p>
        </div>
      )}
    </ul>
  );
};

function getDefaultTaskDateFilter(filterOption: FilterOption): FilterLine {
  const definition = filterOption.definitions.find(
    (x) => x.Field === TaskFieldId.Date.toString()
  )!;
  return {
    id: TaskFieldId.Date.toString(),
    definition,
    kind: definition.Type,
    value: "",
    encodedFilter: "",
    isEmpty: () => true
  };
}

const CalendarView = ({
  selectedDates,
  selectedMonth,
  onDateClick,
  onMonthChange,
  issues
}: {
  selectedDates: Date[];
  selectedMonth: Date;
  onDateClick: (date: Date) => void;
  onMonthChange: (date: Date) => void;
  issues: TaskEntities.ListItem[];
}) => {
  const startOfMonth = moment(selectedMonth).startOf("month");
  const endOfMonth = moment(selectedMonth).endOf("month");

  const issuesDates = issues.map((x) => moment(x.dueDate));

  const days = Array.from({ length: endOfMonth.date() }, (_, i) => {
    const current = startOfMonth.clone().date(i + 1);
    const isSelected = selectedDates.some((d) =>
      moment(d).isSame(current, "day")
    );
    const hasIssue = issuesDates.some((d) => d.isSame(current, "day"));
    let cls = "";
    if (isSelected) cls = "selected";
    else if (hasIssue) cls = "active";
    return { date: current.toDate(), cls };
  });

  const prevMonthDays = Array.from({ length: startOfMonth.weekday() })
    .map((_, idx) =>
      startOfMonth
        .clone()
        .subtract(idx + 1, "days")
        .toDate()
    )
    .reverse();
  const nextMonthCount = 6 - endOfMonth.weekday();
  const nextMonthDays = Array.from({ length: nextMonthCount }, (_, i) => i + 1);

  const advanceMonth = (dir: number) => {
    const newMonth = moment(selectedMonth).add(dir, "month").toDate();
    onMonthChange(newMonth);
  };

  return (
    <div className="calendar">
      <div className="header">
        <div className="d-flex gap-2 align-item-center">
          <div className="year">{selectedMonth.getFullYear()}</div>
          <div className="month">{DateService.getMonthName(selectedMonth)}</div>
        </div>
        <div className="actions">
          <button className="btn btn-link" onClick={() => advanceMonth(-1)}>
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button className="btn btn-link" onClick={() => advanceMonth(1)}>
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
      <div className="grid-container">
        {[
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday"
        ].map((day) => (
          <div className="grid-item day" key={day}>
            {TranslationService.translate[day]}
          </div>
        ))}
        {prevMonthDays.map((d) => (
          <div className="grid-item" key={d.getTime()}>
            <button className="blocked">{d.getDate()}</button>
          </div>
        ))}
        {days.map(({ date, cls }) => (
          <div className="grid-item" key={date.getTime()}>
            <button className={cls} onClick={() => onDateClick(date)}>
              {date.getDate()}
            </button>
          </div>
        ))}
        {nextMonthDays.map((d) => (
          <div className="grid-item" key={d}>
            <button className="blocked">{d}</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CalendarTask;
